import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { ContractLand } from '../types/api';

export const contractsLandKeys = {
  all: ['contracts-land'] as const,
  list: (projectId: string | null, filters: ListFilters) =>
    [...contractsLandKeys.all, 'list', projectId, filters] as const,
  byId: (projectId: string | null, id: string) =>
    [...contractsLandKeys.all, 'byId', projectId, id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useContractsLandList(filters: ListFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: contractsLandKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contracts: ContractLand[] }>('/contracts-land', { ...filters });
      return res.contracts;
    },
  });
}

export function useContractLand(id: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: id ? contractsLandKeys.byId(projectId, id) : ['contracts-land', 'none'],
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ contract: ContractLand }>(`/contracts-land/${id}`);
      return res.contract;
    },
  });
}

export interface ContractLandInput {
  contract_number: string;
  contract_title?: string | null | undefined;
  contract_type?: string | null | undefined;
  prospect?: string | null | undefined;
  field?: string | null | undefined;
  reviewer_user_id?: string | null | undefined;

  party_1?: string | null | undefined;
  other_parties?: string | null | undefined;

  county?: string | null | undefined;
  state?: string | null | undefined;
  township?: string | null | undefined;
  range?: string | null | undefined;
  section?: string | null | undefined;
  block?: string | null | undefined;

  contract_date?: string | null | undefined;

  book?: string | null | undefined;
  page?: string | null | undefined;
  entry?: string | null | undefined;

  flag_for_issues?: string | null | undefined;
  issue_remark?: string | null | undefined;

  description?: string | null | undefined;

  depth_limitations?: boolean | undefined;
  depth_remarks?: string | null | undefined;
  royalty?: boolean | undefined;
  orri?: boolean | undefined;
  non_consent?: boolean | undefined;
  non_consent_penalty?: string | null | undefined;
  call_on_production?: boolean | undefined;
  special_pooling_provisions?: boolean | undefined;
  special_pooling_remarks?: string | null | undefined;
  gas_balancing?: boolean | undefined;

  joa_form_type?: string | null | undefined;
  change_of_operator?: string | null | undefined;

  remarks?: string | null | undefined;
  reviewer_notes?: string | null | undefined;
  change_remarks?: string | null | undefined;
}

export function useCreateContractLand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContractLandInput) =>
      api.post<{ contract: ContractLand }>('/contracts-land', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsLandKeys.all }),
  });
}

export function useUpdateContractLand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ContractLandInput> }) =>
      api.patch<{ contract: ContractLand }>(`/contracts-land/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsLandKeys.all }),
  });
}

export function useDeleteContractLand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/contracts-land/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: contractsLandKeys.all }),
  });
}
